import { useState } from "react";

import { useAuth } from "../hooks/use-auth";
import { describeError } from "../lib/errors";
import { useErrorReporter } from "./error-reporter";

/**
 * Superadmin-only technical error panel.
 *
 * Collapsed by default and rendered in place of nothing at all for everyone
 * else, so the friendly UI is never cluttered for ordinary users. Only the
 * superadmin role sees this, and only the `detail` that `toClientError` chose
 * to release on the server.
 *
 * Mount it once in the root route so it picks up errors from any screen.
 */
export function ErrorDetails({ className = "" }) {
  const { isSuperAdmin } = useAuth();
  const { lastError, clearError } = useErrorReporter();
  const [copied, setCopied] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Two independent gates, both required:
  //   isSuperAdmin - the viewer's role.
  //   lastError     - there is something to show.
  if (!isSuperAdmin || !lastError) return null;

  const { error, eventId, retry } = lastError;
  const view = describeError(error, { isSuperadmin: true, eventId });

  /**
   * Copy a plain-text diagnostics block. Everything here is already
   * superadmin-scoped, so including the cause is intentional -- it is exactly
   * what someone needs to paste into a bug report.
   */
  async function copyDiagnostics() {
    const lines = [
      `code: ${view.code}`,
      `area: ${view.area || "(none)"}`,
      `title: ${view.title}`,
      `message: ${view.message}`,
      `hint: ${view.hint}`,
      `retryable: ${view.retryable}`,
      `occurredAt: ${new Date(view.timestamp).toISOString()}`,
      `sentryEventId: ${view.eventId ?? "(none)"}`,
    ];
    if (view.detail) lines.push(`cause: ${view.detail}`);

    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be denied (insecure origin, permissions). The
      // data is on screen already, so there is nothing useful to do here.
      setCopied(false);
    }
  }

  /**
   * Re-run the operation that failed, when the caller supplied a retry handler.
   * The panel clears itself first so a successful retry does not leave a stale
   * error on screen.
   */
  async function onRetry() {
    if (!retry) return;
    setRetrying(true);
    try {
      await retry();
    } finally {
      setRetrying(false);
      clearError();
    }
  }

  return (
    <details
      className={`rounded-md border border-border bg-surface-2/40 p-4 text-left ${className}`}
    >
      <summary className="cursor-pointer text-sm font-medium text-text">
        Technical details (superadmin only)
      </summary>

      <dl className="mt-3 grid gap-2 text-xs text-muted">
        <div className="flex flex-wrap items-center gap-2">
          <dt className="font-medium">Code</dt>
          <dd>
            <code className="rounded bg-accent px-1.5 py-0.5 font-mono text-accent-foreground">
              {view.code}
            </code>
          </dd>
        </div>

        <div className="flex flex-wrap gap-2">
          <dt className="font-medium">Area</dt>
          <dd className="font-mono">{view.area || "(none)"}</dd>
        </div>

        <div className="flex flex-wrap gap-2">
          <dt className="font-medium">What happened</dt>
          <dd className="text-text">{view.message}</dd>
        </div>

        <div className="flex flex-wrap gap-2">
          <dt className="font-medium">What to do</dt>
          <dd className="text-text">{view.hint}</dd>
        </div>

        <div className="flex flex-wrap gap-2">
          <dt className="font-medium">When</dt>
          <dd>{new Date(view.timestamp).toLocaleString()}</dd>
        </div>

        {view.eventId && (
          <div className="flex flex-wrap gap-2">
            <dt className="font-medium">Sentry</dt>
            <dd className="font-mono">{view.eventId}</dd>
          </div>
        )}

        {view.detail && (
          <div className="mt-1">
            <dt className="font-medium">Cause</dt>
            <dd>
              {/* Plain text, never HTML: the cause is untrusted server output. */}
              <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-bg p-2 font-mono text-text">
                {view.detail}
              </pre>
            </dd>
          </div>
        )}
      </dl>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={copyDiagnostics}
          className="rounded border border-input bg-bg px-2 py-1 text-xs font-medium text-text transition-colors hover:bg-surface-hover"
        >
          {copied ? "Copied" : "Copy diagnostics"}
        </button>

        {/* Only offer retry when the operation is retryable AND the caller
            registered a handler. A disabled button would be a dead end. */}
        {view.retryable && retry && (
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            className="rounded bg-accent px-2 py-1 text-xs font-medium text-accent-foreground transition-colors hover:bg-accent/90 disabled:opacity-60"
          >
            {retrying ? "Retrying…" : "Try again"}
          </button>
        )}

        <button
          type="button"
          onClick={clearError}
          className="rounded border border-input bg-bg px-2 py-1 text-xs font-medium text-text transition-colors hover:bg-surface-hover"
        >
          Dismiss
        </button>
      </div>
    </details>
  );
}
